import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    ArrowLeft, 
    Package, 
    Clock, 
    CheckCircle, 
    Truck, 
    XCircle,
    Building2,
    CreditCard,
    FileText,
    Download,
    Loader2,
    Zap,
    Info,
    Unlock,
    Lock,
    AlertTriangle,
    Battery,
    Wrench,
    Shield,
    Smartphone,
    Globe
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const OrderDetailPage = () => {
    const { orderId } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);
    const [signalingTransfer, setSignalingTransfer] = useState(false);
    const [downloadingProforma, setDownloadingProforma] = useState(false);

    const handleSignalInstantTransfer = async () => {
        if (signalingTransfer) return;
        
        setSignalingTransfer(true);
        try {
            const response = await axios.post(
                `${API_URL}/orders/${orderId}/signal-transfer`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Virement immédiat signalé ! Nous vérifions votre paiement.');
            // Refresh order to show updated state
            fetchOrder();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du signalement');
        } finally {
            setSignalingTransfer(false);
        }
    };

    const fetchOrder = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/orders/${orderId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrder(response.data);
        } catch (error) {
            console.error('Fetch order error:', error);
            navigate('/orders');
        } finally {
            setLoading(false);
        }
    }, [token, orderId, navigate]);

    useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    // Download packing slip
    const handleDownloadPackingSlip = async () => {
        setDownloading('packing');
        try {
            const response = await axios.get(
                `${API_URL}/orders/${orderId}/packing-slip`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bon_commande_${orderId.slice(0,8)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Bon de commande téléchargé');
        } catch (error) {
            toast.error('Erreur lors du téléchargement');
        } finally {
            setDownloading(null);
        }
    };

    // Download invoice
    const handleDownloadInvoice = async () => {
        setDownloading('invoice');
        try {
            const response = await axios.get(
                `${API_URL}/orders/${orderId}/invoice`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            
            // Get filename from Content-Disposition header if available
            const contentDisposition = response.headers['content-disposition'];
            let filename = `Facture_${order?.pennylane_invoice_number || order?.invoice_number || orderId.slice(0,8)}.pdf`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }
            
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Facture téléchargée');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Facture non disponible');
        } finally {
            setDownloading(null);
        }
    };

    // Download proforma
    const handleDownloadProforma = async () => {
        if (!order?.pennylane_proforma_pdf_url) {
            toast.error('Proforma non disponible');
            return;
        }
        
        setDownloadingProforma(true);
        try {
            // Open Pennylane public PDF URL in new tab
            window.open(order.pennylane_proforma_pdf_url, '_blank');
            toast.success('Proforma ouverte dans un nouvel onglet');
        } catch (error) {
            toast.error('Erreur lors du téléchargement de la proforma');
        } finally {
            setDownloadingProforma(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />En attente de paiement</Badge>;
            case 'paid':
                return <Badge className="bg-blue-100 text-blue-800"><CheckCircle className="w-3 h-3 mr-1" />Payée</Badge>;
            case 'shipped':
                return <Badge className="bg-indigo-100 text-indigo-800"><Truck className="w-3 h-3 mr-1" />Expédiée</Badge>;
            case 'delivered':
                return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3 mr-1" />Livrée</Badge>;
            case 'cancelled':
                return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Annulée</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getShippingLabel = (method) => {
        switch (method) {
            case 'pickup': return 'Retrait sur place';
            case 'standard': return 'Livraison standard (3-5 jours)';
            case 'express': return 'Livraison express (24-48h)';
            default: return method;
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
                    <div className="animate-pulse space-y-6">
                        <div className="h-8 bg-slate-200 rounded w-1/4"></div>
                        <div className="h-64 bg-slate-200 rounded"></div>
                    </div>
                </div>
            <Footer />
        </Layout>
        );
    }

    if (!order) {
        return null;
    }

    return (
        <Layout>
            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
                <Button 
                    variant="ghost" 
                    className="mb-6" 
                    onClick={() => navigate('/orders')}
                    data-testid="back-to-orders"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour aux commandes
                </Button>

                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                            Commande #{order.id.slice(0, 8)}
                        </h1>
                        <p className="text-slate-600 mt-1">
                            Passée le {formatDate(order.created_at)}
                        </p>
                    </div>
                    {getStatusBadge(order.status)}
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Left: Items */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Order Items */}
                        <Card className="border-slate-200">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Package className="w-5 h-5" />
                                    Articles ({order.items.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="divide-y divide-slate-200">
                                    {order.items.map((item, index) => (
                                        <div key={index} className="py-5 first:pt-0 last:pb-0">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    {/* Title with storage */}
                                                    <h4 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                                                        <Smartphone className="w-5 h-5 text-slate-400" />
                                                        {item.brand} {item.model}
                                                        {item.storage_capacity && (
                                                            <span className="text-slate-600 font-normal">{item.storage_capacity}Go</span>
                                                        )}
                                                        {item.color && (
                                                            <span className="text-slate-500 font-normal">• {item.color}</span>
                                                        )}
                                                    </h4>
                                                    
                                                    {/* Product characteristics grid - with clear labels */}
                                                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 mt-4 text-sm bg-slate-50 rounded-lg p-4">
                                                        {/* Grade */}
                                                        <div className="flex flex-col">
                                                            <span className="text-xs text-slate-500 uppercase tracking-wide mb-1">Grade</span>
                                                            {item.grade ? (
                                                                <div className="flex items-center gap-2">
                                                                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                                                        item.grade === 'A' ? 'bg-emerald-500' :
                                                                        item.grade === 'B' ? 'bg-blue-500' :
                                                                        item.grade === 'C' ? 'bg-amber-500' :
                                                                        'bg-red-500'
                                                                    }`}>
                                                                        {item.grade}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Version (EU, US, etc.) */}
                                                        <div className="flex flex-col">
                                                            <span className="text-xs text-slate-500 uppercase tracking-wide mb-1">Version</span>
                                                            {(item.version || item.network || item.origin) ? (
                                                                <div className="flex items-center gap-1">
                                                                    <Globe className="w-4 h-4 text-blue-500" />
                                                                    <span className="text-blue-600 font-medium">
                                                                        {item.version || item.network || item.origin}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* État */}
                                                        <div className="flex flex-col">
                                                            <span className="text-xs text-slate-500 uppercase tracking-wide mb-1">État</span>
                                                            {(item.aesthetic_condition || item.erp_etat) ? (
                                                                <div className="flex items-center gap-1">
                                                                    {(item.aesthetic_condition === 'Fonctionnel' || item.erp_etat?.toLowerCase() === 'fonctionnel') ? (
                                                                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                                                                    ) : (
                                                                        <Wrench className="w-4 h-4 text-amber-500" />
                                                                    )}
                                                                    <span className={
                                                                        (item.aesthetic_condition === 'Fonctionnel' || item.erp_etat?.toLowerCase() === 'fonctionnel')
                                                                            ? 'text-emerald-600 font-medium' : 'text-amber-600 font-medium'
                                                                    }>
                                                                        {item.aesthetic_condition || item.erp_etat}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Lock */}
                                                        <div className="flex flex-col">
                                                            <span className="text-xs text-slate-500 uppercase tracking-wide mb-1">Lock</span>
                                                            <div className="flex items-center gap-1">
                                                                {(!item.lock_status || item.lock_status === 'Débloqué' || item.lock_status === '-') ? (
                                                                    <>
                                                                        <Unlock className="w-4 h-4 text-emerald-500" />
                                                                        <span className="text-emerald-600 font-medium">Débloqué</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Lock className="w-4 h-4 text-red-500" />
                                                                        <span className="text-red-600 font-medium">{item.lock_status}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Cosmétique */}
                                                        <div className="flex flex-col">
                                                            <span className="text-xs text-slate-500 uppercase tracking-wide mb-1">Cosm.</span>
                                                            {item.cosmetic_condition ? (
                                                                <div className="flex items-center gap-1">
                                                                    {item.cosmetic_condition === 'OK' ? (
                                                                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                                                                    ) : (
                                                                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                                                                    )}
                                                                    <span className={
                                                                        item.cosmetic_condition === 'OK' ? 'text-emerald-600 font-medium' : 'text-amber-600 font-medium'
                                                                    }>
                                                                        {item.cosmetic_condition}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Batterie */}
                                                        <div className="flex flex-col">
                                                            <span className="text-xs text-slate-500 uppercase tracking-wide mb-1">Batt.</span>
                                                            {item.battery_health ? (() => {
                                                                // Parse battery health from string values like "80_85", "inf_80", "new", "sup_85", etc.
                                                                const bh = String(item.battery_health).toLowerCase();
                                                                let batteryLevel = 'unknown';
                                                                let displayText = item.battery_health;
                                                                
                                                                if (bh === 'new' || bh === 'neuve' || bh === 'sup_85' || bh.includes('>85') || bh.includes('sup')) {
                                                                    batteryLevel = 'high'; // >85%
                                                                    displayText = '>85%';
                                                                } else if (bh === '80_85' || bh.includes('80-85') || bh.includes('80_85')) {
                                                                    batteryLevel = 'medium'; // 80-85%
                                                                    displayText = '80-85%';
                                                                } else if (bh === 'inf_80' || bh.includes('<80') || bh.includes('inf') || bh === 'low') {
                                                                    batteryLevel = 'low'; // <80%
                                                                    displayText = '<80%';
                                                                } else if (bh === 'inconnu' || bh === 'unknown' || bh === '-') {
                                                                    batteryLevel = 'unknown';
                                                                    displayText = '?';
                                                                } else if (!isNaN(parseInt(bh))) {
                                                                    // Numeric value
                                                                    const numVal = parseInt(bh);
                                                                    if (numVal > 85) {
                                                                        batteryLevel = 'high';
                                                                        displayText = '>85%';
                                                                    } else if (numVal >= 80) {
                                                                        batteryLevel = 'medium';
                                                                        displayText = '80-85%';
                                                                    } else {
                                                                        batteryLevel = 'low';
                                                                        displayText = '<80%';
                                                                    }
                                                                }
                                                                
                                                                const colorClass = batteryLevel === 'high' ? 'text-emerald-500' :
                                                                                   batteryLevel === 'medium' ? 'text-amber-500' :
                                                                                   batteryLevel === 'low' ? 'text-red-500' : 'text-slate-400';
                                                                const textColorClass = batteryLevel === 'high' ? 'text-emerald-600' :
                                                                                       batteryLevel === 'medium' ? 'text-amber-600' :
                                                                                       batteryLevel === 'low' ? 'text-red-600' : 'text-slate-500';
                                                                
                                                                return (
                                                                    <div className="flex items-center gap-1">
                                                                        <Battery className={`w-4 h-4 ${colorClass}`} />
                                                                        <span className={`font-medium ${textColorClass}`}>
                                                                            {displayText}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })() : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Pièces */}
                                                        <div className="flex flex-col">
                                                            <span className="text-xs text-slate-500 uppercase tracking-wide mb-1">Pièces</span>
                                                            {item.erp_pieces_incluses ? (
                                                                <div className="flex items-center gap-1">
                                                                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                                                                    <span className="text-emerald-600 font-medium">Incluses</span>
                                                                </div>
                                                            ) : item.missing_parts ? (
                                                                <span className="text-amber-600 font-medium">{item.missing_parts}</span>
                                                            ) : (
                                                                <div className="flex items-center gap-1">
                                                                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                                                                    <span className="text-emerald-600 font-medium">OK</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Pannes (Defects) - with clear title */}
                                                    {item.pannes && item.pannes.length > 0 && item.pannes.filter(p => p && p.toLowerCase() !== 'full ok').length > 0 && (
                                                        <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                                            <div className="flex flex-col gap-1">
                                                                <span className="text-xs text-amber-600 uppercase tracking-wide font-medium">Pannes</span>
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                                                                    <span className="text-sm text-amber-800 font-medium">
                                                                        {item.pannes.filter(p => p && p.toLowerCase() !== 'full ok').join(', ')}
                                                                    </span>
                                                                    {item.has_niveau_3 && (
                                                                        <Badge className="bg-red-100 text-red-700 text-xs">Niveau 3</Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* IMEI and TVA */}
                                                    <div className="flex items-center gap-4 mt-3">
                                                        <p className="text-sm text-slate-500 font-mono">
                                                            IMEI: {item.imei}
                                                        </p>
                                                        <Badge 
                                                            variant="outline" 
                                                            className={item.vat_regime === 'margin' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}
                                                        >
                                                            {item.vat_regime === 'margin' ? 'TVA Marge' : 'TVA 20%'}
                                                        </Badge>
                                                    </div>
                                                </div>
                                                
                                                {/* Price */}
                                                <div className="text-right ml-4">
                                                    {item.original_price && item.original_price > item.price ? (
                                                        <>
                                                            <p className="text-sm text-slate-400 line-through">{item.original_price.toFixed(2)} €</p>
                                                            <p className="font-bold text-green-600 text-xl">{(item.price || 0).toFixed(2)} €</p>
                                                        </>
                                                    ) : (
                                                        <p className="font-bold text-slate-900 text-xl">{(item.price || 0).toFixed(2)} €</p>
                                                    )}
                                                    <p className="text-xs text-slate-500">HT</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Payment Instructions (if pending) */}
                        {order.status === 'pending' && (
                            <Card className="border-amber-200 bg-amber-50">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-amber-800">
                                        <CreditCard className="w-5 h-5" />
                                        Instructions de paiement
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-amber-800 mb-4">
                                        Veuillez effectuer le virement aux coordonnées suivantes :
                                    </p>
                                    <div className="bg-white rounded-lg p-4 space-y-2 text-sm">
                                        <p><span className="text-slate-500">Bénéficiaire:</span> <span className="font-medium">Eco Mobile Solution</span></p>
                                        <p><span className="text-slate-500">Banque:</span> <span className="font-medium">Qonto</span></p>
                                        <p><span className="text-slate-500">IBAN:</span> <span className="font-mono font-medium">FR76 1695 8000 0183 1206 0034 363</span></p>
                                        <p><span className="text-slate-500">BIC:</span> <span className="font-mono font-medium">QNTOFRP1XXX</span></p>
                                        <p><span className="text-slate-500">Référence:</span> <span className="font-mono font-medium text-amber-600">CMD-{order.id.slice(0, 8).toUpperCase()}</span></p>
                                        <p><span className="text-slate-500">Montant:</span> <span className="font-bold">{(order.total_ttc || order.total || 0).toFixed(2)} €</span></p>
                                    </div>
                                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                                        {order.instant_transfer_signaled ? (
                                            <div className="flex items-center gap-2 text-green-700">
                                                <CheckCircle className="w-4 h-4" />
                                                <span className="text-sm font-medium">Virement immédiat signalé le {new Date(order.instant_transfer_signaled_at).toLocaleDateString('fr-FR')}</span>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-blue-800 text-sm mb-3">
                                                    <strong className="flex items-center gap-1"><Zap className="w-4 h-4" /> Virement instantané ?</strong><br />
                                                    Signalez-le pour accélérer le traitement de votre commande.
                                                </p>
                                                <Button
                                                    onClick={handleSignalInstantTransfer}
                                                    disabled={signalingTransfer}
                                                    size="sm"
                                                    className="bg-blue-600 hover:bg-blue-700"
                                                >
                                                    {signalingTransfer ? (
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    ) : (
                                                        <Zap className="w-4 h-4 mr-2" />
                                                    )}
                                                    J'ai effectué un virement immédiat
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                    <div className="mt-3 flex items-start gap-2 text-slate-600">
                                        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                        <p className="text-xs">
                                            <strong>Virement classique ?</strong> Aucune action nécessaire. 
                                            Traitement automatique dès réception (1-2 jours ouvrés).
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Invoice (if available) */}
                        {(order.pennylane_invoice_number || order.pennylane_invoice_id) && (
                            <Card className="border-slate-200">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <FileText className="w-5 h-5" />
                                        Facture
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-slate-600 mb-4">
                                        Votre facture a été générée avec succès.
                                    </p>
                                    <p className="text-sm font-medium">
                                        N° Facture: <span className="font-mono text-lg">{order.pennylane_invoice_number || order.invoice_number}</span>
                                    </p>
                                </CardContent>
                            </Card>
                        )}

                        {/* Tracking Info (if shipped) */}
                        {order.tracking_number && (
                            <Card className="border-indigo-200 bg-indigo-50">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-indigo-800">
                                        <Truck className="w-5 h-5" />
                                        Suivi de livraison
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="bg-white rounded-lg p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-500">N° de suivi</span>
                                            <span className="font-mono font-bold text-indigo-700">{order.tracking_number}</span>
                                        </div>
                                        {order.shipment?.carrier_code && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-slate-500">Transporteur</span>
                                                <span className="font-medium">{order.shipment.carrier_code}</span>
                                            </div>
                                        )}
                                    </div>
                                    {order.tracking_url && (
                                        <a 
                                            href={order.tracking_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors w-full justify-center"
                                        >
                                            <Truck className="w-4 h-4" />
                                            Suivre mon colis
                                        </a>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Right: Summary */}
                    <div className="space-y-6">
                        {/* Order Summary */}
                        <Card className="border-slate-200">
                            <CardHeader>
                                <CardTitle>Récapitulatif</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-600">Sous-total HT ({order.items?.length || 0} articles)</span>
                                    <span>{((order.total_ht || order.subtotal || 0) - (order.shipping_cost || 0)).toFixed(2)} €</span>
                                </div>
                                {(order.shipping_cost || 0) > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-600">Frais de port HT</span>
                                        <span>{(order.shipping_cost || 0).toFixed(2)} €</span>
                                    </div>
                                )}
                                {(order.shipping_cost || 0) === 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-600">Frais de port</span>
                                        <span className="text-green-600 font-medium">Offerts</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-600">TVA articles</span>
                                    <span>{((order.total_vat || 0) - (order.shipping_vat || 0)).toFixed(2)} €</span>
                                </div>
                                {(order.shipping_cost || 0) > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-600">TVA frais de port (20%)</span>
                                        <span>{(order.shipping_vat || (order.shipping_cost || 0) * 0.20).toFixed(2)} €</span>
                                    </div>
                                )}
                                <div className="border-t border-slate-200 pt-4">
                                    <div className="flex justify-between font-bold">
                                        <span>Total TTC</span>
                                        <span>{(order.total_ttc || order.total || 0).toFixed(2)} €</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Documents */}
                        <Card className="border-slate-200">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="w-5 h-5" />
                                    Documents
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {/* Bon de commande - always available */}
                                <Button
                                    variant="outline"
                                    className="w-full justify-start"
                                    onClick={handleDownloadPackingSlip}
                                    disabled={downloading === 'packing'}
                                    data-testid="download-packing-slip"
                                >
                                    {downloading === 'packing' ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Download className="w-4 h-4 mr-2" />
                                    )}
                                    Bon de commande
                                </Button>

                                {/* Proforma - if available */}
                                {order.pennylane_proforma_pdf_url && (
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start border-amber-300 text-amber-700 hover:bg-amber-50"
                                        onClick={handleDownloadProforma}
                                        disabled={downloadingProforma}
                                        data-testid="download-proforma"
                                    >
                                        {downloadingProforma ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Download className="w-4 h-4 mr-2" />
                                        )}
                                        Proforma {order.pennylane_proforma_number || ''}
                                    </Button>
                                )}

                                {/* Facture - only if shipped */}
                                {(order.pennylane_invoice_number || order.invoice_number) ? (
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start"
                                        onClick={handleDownloadInvoice}
                                        disabled={downloading === 'invoice'}
                                        data-testid="download-invoice"
                                    >
                                        {downloading === 'invoice' ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Download className="w-4 h-4 mr-2" />
                                        )}
                                        Facture {order.pennylane_invoice_number || order.invoice_number}
                                    </Button>
                                ) : (
                                    <p className="text-sm text-slate-500 text-center py-2">
                                        Facture disponible après expédition
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Shipping */}
                        <Card className="border-slate-200">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Truck className="w-5 h-5" />
                                    Livraison
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="font-medium">{getShippingLabel(order.shipping_method)}</p>
                            </CardContent>
                        </Card>

                        {/* Company */}
                        <Card className="border-slate-200">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Building2 className="w-5 h-5" />
                                    Facturation
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="font-medium">{order.company_name}</p>
                                <p className="text-sm text-slate-600 mt-1">{order.user_email}</p>
                            </CardContent>
                        </Card>

                        {/* Notes */}
                        {order.notes && (
                            <Card className="border-slate-200">
                                <CardHeader>
                                    <CardTitle>Notes</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-slate-600">{order.notes}</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        <Footer />
        </Layout>
    );
};

export default OrderDetailPage;
